import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { DirectusService } from '../core/services/directus.service';
import { EntityWorkbenchComponent } from '../shared/entity-workbench.component';

@Component({
  selector: 'app-sales-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <section class="orders-page">
      <header class="orders-header">
        <h2>Ticket Orders</h2>
        <a routerLink="/events/published" class="btn-primary">+ Add New Order</a>
      </header>

      <section class="kpi-strip">
        <div class="kpi"><span>Sales</span><strong>{{ totalSalesLabel }}</strong><small>0%</small></div>
        <div class="kpi"><span>Orders</span><strong>{{ totalOrders }}</strong><small>0%</small></div>
        <div class="kpi"><span>Avg. order value</span><strong>{{ avgOrderLabel }}</strong><small>0%</small></div>
        <div class="kpi-actions">
          <button type="button">Last 30 days</button>
          <a routerLink="/sales/analytics/overview">Go to Analytics</a>
        </div>
      </section>

      <section class="table-shell">
        <div class="toolbar">
          <div class="left">
            <select [(ngModel)]="statusFilter">
              <option value="all">All items</option>
              <option value="paid">Paid</option>
              <option value="not_paid">Not paid</option>
              <option value="free">Free</option>
              <option value="processing">Processing</option>
              <option value="removed">Removed</option>
              <option value="authorized">Authorized</option>
            </select>
            <button type="button" class="linkish">Manage View</button>
          </div>
          <div class="right">
            <button type="button" class="btn-outline">Filter</button>
            <button type="button" class="btn-icon">≋</button>
            <input type="search" [(ngModel)]="searchTerm" placeholder="Search..." />
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th class="check"><input type="checkbox" /></th>
                <th>Order</th>
                <th>Date created</th>
                <th>Customer</th>
                <th>Payment</th>
                <th>Fulfillment</th>
                <th>Total</th>
                <th>Items</th>
                <th>Tags</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of filteredRows">
                <td class="check"><input type="checkbox" /></td>
                <td>{{ row.order }}</td>
                <td>{{ row.createdLabel }}</td>
                <td>{{ row.customer }}</td>
                <td><span class="badge payment" [class.unpaid]="row.payment === 'Unpaid'">{{ row.payment }}</span></td>
                <td><span class="badge fulfil" [class.cancelled]="row.fulfilment === 'Cancelled'">{{ row.fulfilment }}</span></td>
                <td>{{ row.totalLabel }}</td>
                <td>{{ row.items }}</td>
                <td>{{ row.tags }}</td>
              </tr>
              <tr *ngIf="!loading && filteredRows.length === 0">
                <td colspan="9" class="empty">No orders found.</td>
              </tr>
              <tr *ngIf="loading">
                <td colspan="9" class="empty">Loading orders...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .orders-page {
      display: grid;
      gap: 14px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
    .orders-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .orders-header h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      letter-spacing: -0.02em;
      font-weight: 700;
    }
    .btn-primary {
      border-radius: 999px;
      border: 1px solid var(--jci-blue);
      background: var(--jci-blue);
      color: var(--jci-white);
      font-weight: 600;
      text-decoration: none;
      padding: 0.52rem 0.9rem;
      font-size: 0.95rem;
    }
    .kpi-strip {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 12px;
      padding: 0.9rem 1rem;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
      gap: 10px;
      align-items: center;
    }
    .kpi {
      display: flex;
      align-items: baseline;
      gap: 8px;
      color: var(--app-text);
    }
    .kpi span {
      color: var(--app-muted);
      font-size: var(--type-body);
    }
    .kpi strong {
      font-size: 1.05rem;
      font-weight: 700;
    }
    .kpi small {
      color: var(--app-muted);
      font-size: 0.78rem;
    }
    .kpi-actions {
      display: flex;
      gap: 12px;
      align-items: center;
      padding-left: 14px;
      border-left: 1px solid var(--app-border);
      white-space: nowrap;
    }
    .kpi-actions button,
    .kpi-actions a {
      background: transparent;
      border: 0;
      color: var(--jci-blue);
      font-size: var(--type-body);
      text-decoration: none;
      cursor: pointer;
    }
    .table-shell {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 12px;
      overflow: hidden;
    }
    .toolbar {
      padding: 0.8rem 1rem;
      display: flex;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid var(--app-border);
    }
    .left,
    .right {
      display: flex;
      gap: 10px;
      align-items: center;
    }
    select,
    input[type='search'] {
      border: 1px solid #cbd5e1;
      background: var(--jci-white);
      color: var(--app-text);
      border-radius: 999px;
      padding: 0.4rem 0.7rem;
      font-size: var(--type-body);
    }
    input[type='search'] {
      min-width: 220px;
    }
    .linkish {
      border: 0;
      background: transparent;
      color: var(--jci-blue);
      font-weight: 500;
      cursor: pointer;
    }
    .btn-outline {
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
      font-weight: 600;
      padding: 0.4rem 0.8rem;
    }
    .btn-icon {
      width: 34px;
      height: 34px;
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
    }
    .table-wrap {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      min-width: 940px;
    }
    thead tr {
      background: #e6f4fb;
    }
    th,
    td {
      padding: 0.62rem 0.65rem;
      border-bottom: 1px solid var(--app-border);
      text-align: left;
      font-size: 0.95rem;
      color: var(--app-text);
      vertical-align: middle;
      white-space: nowrap;
    }
    th.check,
    td.check {
      width: 34px;
      text-align: center;
    }
    .badge {
      border-radius: 4px;
      font-size: 0.78rem;
      font-weight: 600;
      padding: 0.14rem 0.4rem;
      border: 1px solid #d6e4f8;
      color: #264f83;
      background: #eef4ff;
    }
    .badge.payment.unpaid {
      border-color: #ffd2a8;
      color: #c25500;
      background: #fff5eb;
    }
    .badge.fulfil.cancelled {
      border-color: #dad8f4;
      color: #f4f7ff;
      background: #3d466d;
    }
    .empty {
      text-align: center;
      color: var(--app-muted);
      padding: 1rem;
    }
    @media (max-width: 1080px) {
      .kpi-strip {
        grid-template-columns: 1fr;
      }
      .kpi-actions {
        border-left: 0;
        padding-left: 0;
      }
      .toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .right {
        justify-content: flex-start;
      }
      input[type='search'] {
        min-width: 180px;
      }
    }
  `
})
export class SalesOrdersComponent implements OnInit {
  loading = true;
  searchTerm = '';
  statusFilter: 'all' | 'paid' | 'not_paid' | 'free' | 'processing' | 'removed' | 'authorized' = 'all';
  rows: Array<{
    order: string;
    createdLabel: string;
    customer: string;
    payment: 'Paid' | 'Unpaid';
    fulfilment: 'Processing' | 'Cancelled' | '—';
    totalLabel: string;
    items: number;
    tags: string;
    statusRaw: string;
  }> = [];

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  get filteredRows() {
    const needle = this.searchTerm.trim().toLowerCase();
    return this.rows.filter((row) => {
      const matchesStatus =
        this.statusFilter === 'all' ? true : this.matchesFilter(row.statusRaw, this.statusFilter, row.totalLabel);
      if (!matchesStatus) return false;
      if (!needle) return true;
      return [row.order, row.createdLabel, row.customer, row.totalLabel, row.statusRaw]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }

  get totalOrders(): number {
    return this.rows.length;
  }

  get totalSales(): number {
    return this.rows.reduce((sum, row) => {
      const numeric = Number(row.totalLabel.replace(/[^\d.-]+/g, ''));
      return sum + (Number.isFinite(numeric) ? numeric : 0);
    }, 0);
  }

  get totalSalesLabel(): string {
    return this.currency(this.rows.reduce((sum, row) => sum + this.extractNumber(row.totalLabel), 0));
  }

  get avgOrderLabel(): string {
    if (!this.rows.length) return this.currency(0);
    return this.currency(Math.round(this.rows.reduce((sum, row) => sum + this.extractNumber(row.totalLabel), 0) / this.rows.length));
  }

  private extractNumber(label: string): number {
    const clean = label.replace(/[^\d.-]+/g, '');
    const numeric = Number(clean);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const [orders, items] = await Promise.all([
        this.directus.list('event_orders', {
          fields:
            'id,order_number,status,grand_total,currency,date_created,buyer_first_name,buyer_last_name,buyer_guest_contact_id.first_name,buyer_guest_contact_id.last_name,buyer_profile_id.first_name,buyer_profile_id.last_name',
          sort: '-id',
          limit: 200
        }),
        this.directus.list('event_order_items', {
          fields: 'id,order_id,qty',
          limit: 1000
        })
      ]);

      const qtyByOrderId = new Map<string, number>();
      for (const raw of items) {
        const item = raw as Record<string, unknown>;
        const order = item['order_id'];
        const orderId =
          typeof order === 'string'
            ? order
            : order && typeof order === 'object'
              ? String((order as Record<string, unknown>)['id'] ?? '')
              : '';
        if (!orderId) continue;
        const qty = Number(item['qty'] ?? 1);
        qtyByOrderId.set(orderId, (qtyByOrderId.get(orderId) || 0) + (Number.isFinite(qty) ? qty : 1));
      }

      this.rows = orders.map((raw, idx) => {
        const row = raw as Record<string, unknown>;
        const orderId = String(row['id'] ?? '');
        const orderNumber = String(row['order_number'] ?? '').trim() || `#${idx + 1}`;
        const statusRaw = String(row['status'] ?? '').toLowerCase();
        const payment = statusRaw === 'paid' || statusRaw === 'free' ? 'Paid' : 'Unpaid';
        const fulfilment = statusRaw === 'cancelled' || statusRaw === 'canceled' ? 'Cancelled' : statusRaw === 'processing' ? 'Processing' : '—';

        const buyerFirst =
          String(row['buyer_first_name'] ?? '').trim() ||
          String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim() ||
          String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim();
        const buyerLast =
          String(row['buyer_last_name'] ?? '').trim() ||
          String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim() ||
          String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim();
        const customer = [buyerFirst, buyerLast].filter(Boolean).join(' ').trim() || 'Guest';

        const total = Number(row['grand_total'] ?? 0);
        const created = String(row['date_created'] ?? '').trim();

        return {
          order: orderNumber,
          createdLabel: created ? this.dateTime(created) : '—',
          customer,
          payment,
          fulfilment,
          totalLabel: this.currency(Number.isFinite(total) ? total : 0),
          items: qtyByOrderId.get(orderId) || 0,
          tags: '',
          statusRaw
        };
      });
    } finally {
      this.loading = false;
    }
  }

  private currency(value: number): string {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);
  }

  private dateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  }

  private matchesFilter(statusRaw: string, filter: string, totalLabel: string): boolean {
    if (filter === 'paid') return statusRaw === 'paid';
    if (filter === 'not_paid') return statusRaw === 'pending' || statusRaw === 'processing' || statusRaw === 'expired';
    if (filter === 'free') return this.extractNumber(totalLabel) <= 0;
    if (filter === 'processing') return statusRaw === 'processing' || statusRaw === 'pending';
    if (filter === 'removed') return statusRaw === 'cancelled' || statusRaw === 'canceled' || statusRaw === 'refunded';
    if (filter === 'authorized') return statusRaw === 'authorized';
    return true;
  }
}

@Component({
  selector: 'app-sales-merchandise-future',
  standalone: true,
  template: `
    <section class="placeholder-card">
      <div class="placeholder-head">
        <h2>Orders</h2>
        <span class="coming-soon">Coming soon</span>
      </div>
      <p>Merchandise orders and fulfillment will be added here.</p>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .placeholder-card {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 14px;
      padding: 1rem;
    }
    h2 {
      margin: 0 0 0.35rem;
      font-size: 1.1rem;
    }
    .placeholder-head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.35rem;
    }
    .placeholder-head h2 {
      margin: 0;
    }
    .coming-soon {
      border: 1px solid #b7dff0;
      background: #eef6ff;
      color: var(--jci-blue);
      font-size: 0.76rem;
      font-weight: 700;
      border-radius: 999px;
      padding: 0.1rem 0.5rem;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    p {
      margin: 0;
      color: var(--app-muted);
      font-size: 0.92rem;
    }
  `
})
export class SalesMerchandiseFutureComponent {}

@Component({
  selector: 'app-sales-events',
  standalone: true,
  imports: [EntityWorkbenchComponent],
  template: `
    <app-entity-workbench
      title="Event Orders"
      description="Transactions from event ticket orders."
      collection="event_orders"
      sort="-id"
      [fields]="fields"
    />
  `
})
export class SalesEventsComponent {
  readonly fields = [
    { key: 'order_number', label: 'Order number' },
    { key: 'event_id', label: 'Event' },
    { key: 'status', label: 'Status' },
    { key: 'currency', label: 'Currency' },
    { key: 'grand_total', label: 'Total', type: 'number' as const },
    { key: 'date_created', label: 'Created' }
  ];
}

@Component({
  selector: 'app-sales-memberships',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <section class="memberships-page">
      <header class="memberships-header">
        <div>
          <h2>Memberships</h2>
          <p>All membership records from membership history.</p>
        </div>
      </header>

      <section class="stats-card">
        <div class="stats-head">
          <h3>Statistics</h3>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <strong>{{ totalSubscriptions }}</strong>
            <span>Total records</span>
          </div>
          <div class="stat-item">
            <strong>{{ activeSubscriptions }}</strong>
            <span>Active</span>
          </div>
          <div class="stat-item">
            <strong>{{ pendingSubscriptions }}</strong>
            <span>Pending payment</span>
          </div>
        </div>
      </section>

      <section class="table-shell">
        <div class="toolbar">
          <div class="left">
            <select [(ngModel)]="statusFilter">
              <option value="all">All items</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="pending">Pending Payment</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select [(ngModel)]="yearFilter">
              <option value="all">All years</option>
              <option *ngFor="let y of availableYears" [value]="y">{{ y }}</option>
            </select>
          </div>
          <div class="right">
            <input type="search" [(ngModel)]="searchTerm" placeholder="Search member..." />
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Year</th>
                <th>Type</th>
                <th>Status</th>
                <th>Valid from</th>
                <th>Valid until</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of filteredRows; let i = index" class="clickable-row" (click)="openRow(row)">
                <td class="customer-cell">
                  <span class="avatar" [class.alt]="i % 3 === 1" [class.alt-2]="i % 3 === 2">{{ row.initials }}</span>
                  <span class="customer-name">{{ row.customer }}</span>
                </td>
                <td>{{ row.year }}</td>
                <td>
                  <div class="sub-detail-name">{{ row.membershipType }}</div>
                </td>
                <td>
                  <span class="badge"
                    [class.badge-active]="row.status === 'active'"
                    [class.badge-expired]="row.status === 'expired'"
                    [class.badge-pending]="row.status === 'pending'"
                    [class.badge-cancelled]="row.status === 'cancelled'">
                    {{ row.statusLabel }}
                  </span>
                </td>
                <td>{{ row.startDate }}</td>
                <td>{{ row.endDate }}</td>
                <td>
                  <span class="badge badge-paid" [class.badge-unpaid]="row.paymentStatus === 'Unpaid'" [class.badge-pending-pay]="row.paymentStatus === 'Pending'">
                    {{ row.paymentStatus }}
                  </span>
                </td>
              </tr>
              <tr *ngIf="!loading && filteredRows.length === 0">
                <td colspan="7" class="empty">No membership records found.</td>
              </tr>
              <tr *ngIf="loading">
                <td colspan="7" class="empty">Loading memberships...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .memberships-page {
      display: grid;
      gap: 14px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .memberships-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
    }

    .memberships-header h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      letter-spacing: -0.02em;
    }

    .memberships-header p {
      margin: 0.25rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .btn-primary {
      border-radius: 999px;
      border: 1px solid var(--app-primary);
      background: var(--app-primary);
      color: var(--jci-white);
      font-weight: 600;
      text-decoration: none;
      padding: 0.52rem 0.9rem;
      font-size: 0.95rem;
      white-space: nowrap;
    }

    .btn-outline {
      border: 1px solid var(--app-border);
      border-radius: 999px;
      background: var(--app-surface-soft);
      color: var(--app-primary);
      font-weight: 600;
      padding: 0.4rem 0.85rem;
      font-size: 0.95rem;
      white-space: nowrap;
    }

    .stats-card {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 12px;
      overflow: hidden;
    }

    .stats-head {
      padding: 0.9rem 1rem;
      border-bottom: 1px solid var(--app-border);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
    }

    .stats-head h3 {
      margin: 0;
      font-size: var(--type-subsection-title);
    }

    .stats-link {
      border: 1px solid var(--app-border);
      border-radius: 999px;
      background: var(--app-surface-soft);
      color: var(--app-primary);
      font-weight: 600;
      padding: 0.4rem 0.85rem;
      font-size: var(--type-body);
      text-decoration: none;
      white-space: nowrap;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
    }

    .stat-item {
      display: grid;
      place-items: center;
      gap: 2px;
      padding: 1rem 0.75rem;
      border-right: 1px solid var(--app-border);
    }

    .stat-item:last-child {
      border-right: 0;
    }

    .stat-item strong {
      font-size: 2rem;
      line-height: 1;
      font-weight: 800;
    }

    .stat-item span {
      color: var(--app-muted);
      font-size: var(--type-body);
    }

    .table-shell {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 12px;
      overflow: hidden;
    }

    .toolbar {
      padding: 0.8rem 1rem;
      display: flex;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid var(--app-border);
    }

    .left,
    .right {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    select,
    input[type='search'] {
      border: 1px solid var(--app-border);
      background: var(--jci-white);
      color: var(--app-text);
      border-radius: 999px;
      padding: 0.4rem 0.7rem;
      font-size: var(--type-body);
    }

    input[type='search'] {
      min-width: 230px;
    }

    .linkish {
      border: 0;
      background: transparent;
      color: var(--app-primary);
      font-weight: 500;
      cursor: pointer;
      font-size: var(--type-body);
    }

    .btn-icon {
      width: 34px;
      height: 34px;
      border: 1px solid var(--app-border);
      border-radius: 999px;
      background: var(--app-surface-soft);
      color: var(--app-primary);
    }

    .table-wrap {
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1080px;
    }

    thead tr {
      background: #e6f4fb;
    }

    th,
    td {
      padding: 0.75rem 0.72rem;
      border-bottom: 1px solid var(--app-border);
      text-align: left;
      font-size: var(--type-body);
      color: var(--app-text);
      vertical-align: middle;
      white-space: nowrap;
    }

    .customer-cell {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 240px;
    }

    .avatar {
      width: 44px;
      height: 44px;
      border-radius: 999px;
      background: linear-gradient(135deg, var(--jci-blue), #1fb3e7);
      color: var(--jci-white);
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 0.95rem;
    }

    .avatar.alt {
      background: linear-gradient(135deg, var(--jci-navy), #2d5fad);
    }

    .avatar.alt-2 {
      background: linear-gradient(135deg, var(--jci-teal), var(--jci-blue));
    }

    .customer-name {
      display: inline-block;
      max-width: 220px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .sub-detail-name {
      max-width: 240px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .sub-detail-type {
      color: var(--app-muted);
      font-size: var(--type-body);
    }

    .badge {
      border-radius: 3px;
      font-size: 0.78rem;
      font-weight: 700;
      padding: 0.14rem 0.48rem;
      border: 1px solid #c4c4c4;
      color: #666;
      background: #f5f5f5;
    }

    .badge.badge-active {
      border-color: #8fd7a9;
      color: #16803e;
      background: #ebfff2;
    }

    .badge.badge-expired {
      border-color: #c4c4c4;
      color: #666;
      background: #f5f5f5;
    }

    .badge.badge-pending {
      border-color: #f5d96b;
      color: #92600a;
      background: #fffbeb;
    }

    .badge.badge-cancelled {
      border-color: #fca5a5;
      color: #dc2626;
      background: #fff5f5;
    }

    .badge.badge-paid {
      border-color: #2da463;
      background: #2da463;
      color: #fff;
    }

    .badge.badge-unpaid {
      border-color: #f7b57d;
      background: #fff5eb;
      color: #c25500;
    }

    .badge.badge-pending-pay {
      border-color: #f5d96b;
      background: #fffbeb;
      color: #92600a;
    }

    .clickable-row {
      cursor: pointer;
      transition: background 0.12s;
    }

    .clickable-row:hover {
      background: #f0f8ff;
    }

    .empty {
      text-align: center;
      color: var(--app-muted);
      padding: 1rem;
    }

    @media (max-width: 1080px) {
      .memberships-header {
        flex-direction: column;
      }
      .stats-head {
        flex-direction: column;
        align-items: flex-start;
      }
      .toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .right {
        justify-content: flex-start;
      }
    }
  `
})
export class SalesMembershipsComponent implements OnInit {
  loading = true;
  searchTerm = '';
  statusFilter: 'all' | 'active' | 'expired' | 'pending' | 'cancelled' = 'all';
  yearFilter: 'all' | number = 'all';
  rows: Array<{
    id: string;
    profileId: string;
    customer: string;
    initials: string;
    year: number;
    membershipType: string;
    category: string;
    status: string;
    statusLabel: string;
    startDate: string;
    endDate: string;
    paymentStatus: 'Paid' | 'Unpaid' | 'Pending';
  }> = [];

  constructor(private readonly directus: DirectusService, private readonly router: Router) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  get availableYears(): number[] {
    const years = [...new Set(this.rows.map((r) => r.year))].filter(Boolean);
    return years.sort((a, b) => b - a);
  }

  get filteredRows() {
    const needle = this.searchTerm.trim().toLowerCase();
    return this.rows.filter((row) => {
      if (this.statusFilter !== 'all' && row.status !== this.statusFilter) return false;
      if (this.yearFilter !== 'all' && row.year !== this.yearFilter) return false;
      if (!needle) return true;
      return [row.customer, String(row.year), row.membershipType, row.category, row.statusLabel, row.paymentStatus]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }

  get totalSubscriptions(): number { return this.rows.length; }
  get activeSubscriptions(): number { return this.rows.filter((r) => r.status === 'active').length; }
  get pendingSubscriptions(): number { return this.rows.filter((r) => r.status === 'pending').length; }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const records = await this.directus.list('membership_history', {
        fields: 'id,profile_id.id,profile_id.first_name,profile_id.last_name,year,membership_type,category,status,valid_from,valid_until,paid_at,payment_method',
        sort: '-year,-date_created',
        limit: 500
      });

      const statusLabels: Record<string, string> = {
        active: 'Active', expired: 'Expired', pending: 'Pending Payment', cancelled: 'Cancelled'
      };
      const typeLabels: Record<string, string> = {
        individual: 'Individual', couple: 'Couple'
      };
      const categoryLabels: Record<string, string> = {
        standard: 'Standard', partner: 'Partner'
      };

      this.rows = records.map((raw) => {
        const row = raw as Record<string, unknown>;
        const profile = (row['profile_id'] as Record<string, unknown> | undefined) || {};
        const firstName = String(profile['first_name'] ?? '').trim();
        const lastName = String(profile['last_name'] ?? '').trim();
        const customer = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Unknown Member';

        const status = String(row['status'] ?? 'pending').trim();
        const paidAt = String(row['paid_at'] ?? '').trim();
        const paymentStatus: 'Paid' | 'Unpaid' | 'Pending' =
          paidAt ? 'Paid' : status === 'pending' ? 'Pending' : 'Unpaid';

        const validFrom = String(row['valid_from'] ?? '').trim();
        const validUntil = String(row['valid_until'] ?? '').trim();
        const catRaw = String(row['category'] ?? '').trim();
        const typeRaw = String(row['membership_type'] ?? '').trim();

        return {
          id: String(row['id'] ?? ''),
          profileId: String(profile['id'] ?? ''),
          customer,
          initials: this.initials(customer),
          year: Number(row['year'] ?? 0),
          membershipType: typeLabels[typeRaw] || typeRaw || 'Individual',
          category: categoryLabels[catRaw] || catRaw || '',
          status,
          statusLabel: statusLabels[status] || status,
          startDate: validFrom ? this.formatDate(new Date(validFrom)) : '—',
          endDate: validUntil ? this.formatDate(new Date(validUntil)) : '—',
          paymentStatus
        };
      });
    } finally {
      this.loading = false;
    }
  }

  openRow(row: { profileId: string }): void {
    if (row.profileId) void this.router.navigate(['/members', row.profileId]);
  }

  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  }

  private initials(name: string): string {
    return name.split(/\s+/).filter(Boolean).slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '').join('') || 'NA';
  }
}

@Component({
  selector: 'app-sales-payments-all',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="payments-page">
      <header class="payments-header">
        <h2>Payments</h2>
        <p>Keep track of payments from your customers.</p>
      </header>

      <section class="overview-card">
        <div class="overview-head">
          <span>Overview of <button type="button" class="link-btn">Today</button></span>
        </div>
        <div class="overview-grid">
          <div class="overview-item">
            <div class="label">Gross volume</div>
            <div class="value">{{ grossVolumeLabel }}</div>
          </div>
          <div class="overview-item">
            <div class="label">Successful payments</div>
            <div class="value">{{ successfulPayments }}</div>
          </div>
        </div>
      </section>

      <section class="table-shell">
        <div class="table-head">
          <h3>All payments</h3>
          <div class="table-actions">
            <button type="button" class="btn-outline">Filter</button>
            <button type="button" class="btn-icon">⇩</button>
            <input type="search" [(ngModel)]="searchTerm" placeholder="Name, payment ID, and more" />
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Payment date</th>
                <th>Customer</th>
                <th>Product/Service</th>
                <th>Payment method</th>
                <th>Transaction status</th>
                <th>Transaction date</th>
                <th class="amount-col">Amount</th>
                <th class="arrow-col"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of filteredRows">
                <td>{{ row.paymentDate }}</td>
                <td>{{ row.customer }}</td>
                <td class="truncate-cell">{{ row.product }}</td>
                <td class="truncate-cell">{{ row.paymentMethod }}</td>
                <td>
                  <span class="status-badge" [class.ok]="row.transactionStatus === 'Successful'" [class.warn]="row.transactionStatus !== 'Successful'">
                    {{ row.transactionStatus }}
                  </span>
                </td>
                <td>{{ row.transactionDate }}</td>
                <td class="amount-col">{{ row.amountLabel }}</td>
                <td class="arrow-col">›</td>
              </tr>
              <tr *ngIf="!loading && filteredRows.length === 0">
                <td colspan="8" class="empty">No payments found.</td>
              </tr>
              <tr *ngIf="loading">
                <td colspan="8" class="empty">Loading payments...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .payments-page {
      display: grid;
      gap: 14px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .payments-header h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      letter-spacing: -0.02em;
      font-weight: 700;
    }

    .payments-header p {
      margin: 0.3rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }

    .overview-card {
      border: 1px solid var(--app-border);
      border-radius: 12px;
      background: var(--app-surface);
      overflow: hidden;
    }

    .overview-head {
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      color: var(--app-text);
      font-size: var(--type-body);
    }

    .link-btn {
      border: 0;
      background: transparent;
      color: var(--jci-blue);
      cursor: pointer;
      font-size: inherit;
      padding: 0;
    }

    .overview-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    .overview-item {
      padding: 0.8rem 1rem;
      border-right: 1px solid var(--app-border);
    }

    .overview-item:last-child {
      border-right: 0;
    }

    .label {
      color: var(--app-muted);
      font-size: var(--type-body);
      margin-bottom: 0.25rem;
    }

    .value {
      color: var(--app-text);
      font-size: 1.9rem;
      line-height: 1.1;
      font-weight: 700;
    }

    .table-shell {
      border: 1px solid var(--app-border);
      border-radius: 12px;
      background: var(--app-surface);
      overflow: hidden;
    }

    .table-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
    }

    .table-head h3 {
      margin: 0;
      font-size: var(--type-section-title);
      line-height: 1.1;
      letter-spacing: -0.015em;
      font-weight: 700;
    }

    .table-actions {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .btn-outline {
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
      font-weight: 600;
      padding: 0.4rem 0.8rem;
      white-space: nowrap;
    }

    .btn-icon {
      width: 34px;
      height: 34px;
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
    }

    input[type='search'] {
      border: 1px solid #cbd5e1;
      background: var(--jci-white);
      color: var(--app-text);
      border-radius: 999px;
      padding: 0.4rem 0.72rem;
      min-width: 260px;
      font-size: var(--type-body);
    }

    .table-wrap {
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1120px;
    }

    thead tr {
      background: #e6f4fb;
    }

    th,
    td {
      padding: 0.68rem 0.72rem;
      border-bottom: 1px solid var(--app-border);
      text-align: left;
      font-size: var(--type-body);
      color: var(--app-text);
      vertical-align: middle;
      white-space: nowrap;
    }

    .truncate-cell {
      max-width: 210px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .amount-col {
      text-align: right;
      font-weight: 700;
    }

    .arrow-col {
      width: 28px;
      text-align: center;
      color: var(--jci-blue);
      font-size: 1.15rem;
    }

    .status-badge {
      border-radius: 3px;
      font-size: 0.9rem;
      padding: 0.2rem 0.7rem;
      border: 1px solid #f7b57d;
      background: #fff5eb;
      color: #c25500;
      display: inline-block;
    }

    .status-badge.ok {
      border-color: #8fd7a9;
      background: #ebfff2;
      color: #16803e;
    }

    .status-badge.warn {
      border-color: #f7b57d;
      background: #fff5eb;
      color: #c25500;
    }

    .empty {
      text-align: center;
      color: var(--app-muted);
      padding: 1rem;
    }

    @media (max-width: 1080px) {
      .overview-grid {
        grid-template-columns: 1fr;
      }

      .overview-item {
        border-right: 0;
        border-top: 1px solid var(--app-border);
      }

      .overview-item:first-child {
        border-top: 0;
      }

      .table-head {
        flex-direction: column;
        align-items: flex-start;
      }

      .table-actions {
        width: 100%;
      }

      input[type='search'] {
        min-width: 180px;
        flex: 1;
      }
    }
  `
})
export class SalesPaymentsAllComponent implements OnInit {
  loading = true;
  searchTerm = '';
  rows: Array<{
    paymentDate: string;
    customer: string;
    product: string;
    paymentMethod: string;
    transactionStatus: 'Successful' | 'Pending' | 'Failed';
    transactionDate: string;
    amountValue: number;
    amountLabel: string;
    sortTs: number;
  }> = [];

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  get filteredRows() {
    const needle = this.searchTerm.trim().toLowerCase();
    if (!needle) return this.rows;
    return this.rows.filter((row) =>
      [row.customer, row.product, row.paymentMethod, row.transactionStatus, row.paymentDate, row.amountLabel]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }

  get grossVolumeLabel(): string {
    const total = this.rows.reduce((sum, row) => sum + row.amountValue, 0);
    return this.currency(total);
  }

  get successfulPayments(): number {
    return this.rows.filter((row) => row.transactionStatus === 'Successful').length;
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const [eventOrders, memberships] = await Promise.all([
        this.directus.list('event_orders', {
          fields:
            'id,order_number,status,grand_total,currency,date_created,event_id.name,buyer_first_name,buyer_last_name,buyer_guest_contact_id.first_name,buyer_guest_contact_id.last_name,buyer_profile_id.first_name,buyer_profile_id.last_name,payment_method',
          sort: '-date_created',
          limit: 500
        }),
        this.directus.list('subscription', {
          fields: 'id,profile_id.first_name,profile_id.last_name,profile_name,email,type,total,paid,created_at,payment_method',
          sort: '-created_at',
          limit: 500
        })
      ]);

      const fromEventOrders = eventOrders.map((raw) => {
        const row = raw as Record<string, unknown>;
        const eventRel = (row['event_id'] as Record<string, unknown> | undefined) || {};
        const productName = String(eventRel['name'] ?? '').trim() || 'Event ticket order';
        const statusRaw = String(row['status'] ?? '').toLowerCase();
        const created = String(row['date_created'] ?? '').trim();
        const buyerFirst =
          String(row['buyer_first_name'] ?? '').trim() ||
          String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim() ||
          String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim();
        const buyerLast =
          String(row['buyer_last_name'] ?? '').trim() ||
          String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim() ||
          String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim();

        const amount = Number(row['grand_total'] ?? 0);
        return {
          paymentDate: created ? this.dateOnly(created) : '—',
          customer: [buyerFirst, buyerLast].filter(Boolean).join(' ').trim() || 'Guest',
          product: productName,
          paymentMethod: String(row['payment_method'] ?? '').trim() || 'Manual payment',
          transactionStatus: this.mapEventStatus(statusRaw),
          transactionDate: created ? this.dateOnly(created) : '—',
          amountValue: Number.isFinite(amount) ? amount : 0,
          amountLabel: this.currency(Number.isFinite(amount) ? amount : 0),
          sortTs: created ? new Date(created).getTime() : 0
        };
      });

      const fromMemberships = memberships.map((raw) => {
        const row = raw as Record<string, unknown>;
        const profile = (row['profile_id'] as Record<string, unknown> | undefined) || {};
        const first = String(profile['first_name'] ?? '').trim();
        const last = String(profile['last_name'] ?? '').trim();
        const profileName = String(row['profile_name'] ?? '').trim();
        const email = String(row['email'] ?? '').trim();
        const customer = [first, last].filter(Boolean).join(' ').trim() || profileName || email || 'Member';
        const created = String(row['created_at'] ?? '').trim();
        const amount = Number(row['total'] ?? 0);
        const paid = Boolean(row['paid'] ?? false);
        return {
          paymentDate: created ? this.dateOnly(created) : '—',
          customer,
          product: String(row['type'] ?? '').trim() || 'Membership subscription',
          paymentMethod: String(row['payment_method'] ?? '').trim() || 'Membership payment',
          transactionStatus: paid ? ('Successful' as const) : ('Pending' as const),
          transactionDate: created ? this.dateOnly(created) : '—',
          amountValue: Number.isFinite(amount) ? amount : 0,
          amountLabel: this.currency(Number.isFinite(amount) ? amount : 0),
          sortTs: created ? new Date(created).getTime() : 0
        };
      });

      this.rows = [...fromEventOrders, ...fromMemberships].sort((a, b) => b.sortTs - a.sortTs);
    } finally {
      this.loading = false;
    }
  }

  private mapEventStatus(status: string): 'Successful' | 'Pending' | 'Failed' {
    if (status === 'paid' || status === 'success' || status === 'succeeded' || status === 'free') return 'Successful';
    if (status === 'cancelled' || status === 'canceled' || status === 'failed' || status === 'refunded') return 'Failed';
    return 'Pending';
  }

  private currency(value: number): string {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);
  }

  private dateOnly(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
  }
}

@Component({
  selector: 'app-sales-payments-receipts-future',
  standalone: true,
  template: `
    <section class="placeholder-card">
      <div class="placeholder-head">
        <h2>Receipts</h2>
        <span class="coming-soon">Coming soon</span>
      </div>
      <p>Merchandise store payment receipts will be managed here.</p>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .placeholder-card {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 14px;
      padding: 1rem;
    }
    h2 {
      margin: 0 0 0.35rem;
      font-size: 1.1rem;
    }
    .placeholder-head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.35rem;
    }
    .placeholder-head h2 {
      margin: 0;
    }
    .coming-soon {
      border: 1px solid #b7dff0;
      background: #eef6ff;
      color: var(--jci-blue);
      font-size: 0.76rem;
      font-weight: 700;
      border-radius: 999px;
      padding: 0.1rem 0.5rem;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    p {
      margin: 0;
      color: var(--app-muted);
      font-size: 0.92rem;
    }
  `
})
export class SalesPaymentsReceiptsFutureComponent {}

@Component({
  selector: 'app-sales-analytics-overview',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="overview-page">
      <header class="page-head">
        <div>
          <h2>Sales Overview</h2>
          <p>Analyze your sales performance and gain insights on your customers. <a href="#">Learn more</a></p>
        </div>
        <div class="head-actions">
          <button type="button" class="icon-btn">↻</button>
          <button type="button" class="pill-btn">✉ Subscribe</button>
        </div>
      </header>

      <section class="filter-strip card">
        <select [(ngModel)]="period">
          <option value="30">Last 30 days (Jan 30 - Today)</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last 12 months</option>
        </select>
        <span class="compare-copy">compared to previous period (Dec 31, 2025 - Jan 29, 2026)</span>
      </section>

      <section class="grid-2-1">
        <article class="card">
          <div class="kpi-row">
            <div>
              <span>Total sales</span>
              <strong>{{ salesLabel }}</strong>
            </div>
            <div>
              <span>Tickets sold</span>
              <strong>{{ ticketsSold }}</strong>
            </div>
            <div>
              <span>New subscriptions</span>
              <strong>{{ newSubscriptions }}</strong>
            </div>
          </div>
          <div class="chart-wrap">
            <div class="line-chart">
              <div *ngFor="let point of dailySales; let i = index" class="line-dot" [style.left.%]="(i * 100) / (dailySales.length - 1 || 1)" [style.bottom.%]="point.pct"></div>
            </div>
          </div>
          <a href="#" class="view-link">View Report</a>
        </article>

        <article class="card side-empty">
          <h3>Sales by source and category</h3>
          <div class="empty-block">
            <div class="emoji">🛒</div>
            <strong>No traffic sources available</strong>
            <p>Traffic sources for sales in this period couldn't be tracked.</p>
          </div>
        </article>
      </section>

      <section class="grid-2-1">
        <article class="card">
          <div class="tab-row">
            <button type="button" [class.active]="topTab === 'items'" (click)="topTab = 'items'">Top selling items</button>
            <button type="button" [class.active]="topTab === 'events'" (click)="topTab = 'events'">Top selling events</button>
            <button type="button" [class.active]="topTab === 'subs'" (click)="topTab = 'subs'">Top paying subscriptions</button>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Item name</th>
                  <th>Quantity</th>
                  <th>% of total</th>
                  <th>Change</th>
                  <th>Gross sales</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of topItems">
                  <td>{{ row.name }}</td>
                  <td>{{ row.qty }}</td>
                  <td>{{ row.pct }}%</td>
                  <td>-</td>
                  <td class="money">{{ row.salesLabel }}</td>
                </tr>
                <tr *ngIf="topItems.length === 0">
                  <td colspan="5" class="empty">No item data.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <a href="#" class="view-link">View Report</a>
        </article>

        <article class="card right-stack-card">
          <h3>Top paying customers</h3>
          <div *ngFor="let row of topCustomers" class="customer-row">
            <span class="avatar">{{ row.initials }}</span>
            <div class="customer-meta">
              <div>{{ row.name }}</div>
              <small>Orders: {{ row.orders }}</small>
            </div>
            <strong>{{ row.salesLabel }}</strong>
          </div>
          <div *ngIf="topCustomers.length === 0" class="empty">No customer data.</div>
          <a href="#" class="view-link">View Report</a>
        </article>
      </section>

      <section class="grid-2-1">
        <article class="card">
          <h3>Sales by billing location: country</h3>
          <div class="map-shell">
            <div class="map-placeholder">🌍</div>
            <div class="country-list">
              <strong>Countries</strong>
              <div class="country-row">
                <span>Unknown</span>
                <span>{{ salesLabel }}</span>
              </div>
            </div>
          </div>
          <a href="#" class="view-link">View Report</a>
        </article>

        <div class="right-stack">
          <article class="card donut-card">
            <h3>Sales breakdown by coupon</h3>
            <div class="donut-row">
              <div class="donut"><span>{{ salesLabel }}</span></div>
              <div class="legend">
                <div><i></i> No coupon</div>
                <small>100% · {{ salesLabel }}</small>
              </div>
            </div>
            <a href="#" class="view-link">View Report</a>
          </article>
          <article class="card donut-card">
            <h3>New vs returning customers</h3>
            <div class="donut-row">
              <div class="donut"><span>{{ topCustomers.length }}</span></div>
              <div class="legend">
                <div><i></i> New</div>
                <small>100% · {{ topCustomers.length }}</small>
                <div><i class="alt"></i> Returning</div>
                <small>0% · 0</small>
              </div>
            </div>
            <a href="#" class="view-link">View Report</a>
          </article>
        </div>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }
    .overview-page {
      display: grid;
      gap: 14px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
    .page-head {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: flex-start;
    }
    .page-head h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      letter-spacing: -0.02em;
      font-weight: 700;
    }
    .page-head p {
      margin: 0.3rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }
    .page-head p a {
      color: var(--app-primary);
      text-decoration: none;
    }
    .head-actions {
      display: flex;
      gap: 8px;
    }
    .icon-btn,
    .pill-btn {
      border: 1px solid var(--app-border);
      background: var(--app-surface-soft);
      color: var(--app-primary);
      border-radius: 999px;
      height: 36px;
      padding: 0 14px;
      font-weight: 600;
      font-size: var(--type-body);
    }
    .icon-btn {
      width: 36px;
      padding: 0;
    }
    .card {
      border: 1px solid var(--app-border);
      border-radius: 12px;
      background: var(--app-surface);
      overflow: hidden;
    }
    .filter-strip {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0.95rem 1rem;
    }
    .filter-strip select {
      border: 1px solid var(--app-border);
      background: var(--jci-white);
      color: var(--app-text);
      border-radius: 8px;
      padding: 0.45rem 0.65rem;
      min-width: 350px;
      font-size: var(--type-body);
    }
    .compare-copy {
      color: var(--app-muted);
      font-size: var(--type-body);
    }
    .grid-2-1 {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 14px;
      align-items: stretch;
    }
    .kpi-row {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 0;
      padding: 0.95rem 1rem;
      border-bottom: 1px solid var(--app-border);
    }
    .kpi-row span {
      color: var(--app-muted);
      font-size: var(--type-body);
      display: block;
      margin-bottom: 2px;
    }
    .kpi-row strong {
      font-size: 1.95rem;
      line-height: 1.05;
      font-weight: 700;
      color: var(--app-text);
    }
    .chart-wrap {
      padding: 0.75rem 1rem 0.35rem;
      min-height: 260px;
    }
    .line-chart {
      position: relative;
      height: 220px;
      border-bottom: 2px solid #d8e2ee;
      background-image: linear-gradient(to top, transparent 24%, #eef3f8 24%, #eef3f8 25%, transparent 25%, transparent 49%, #eef3f8 49%, #eef3f8 50%, transparent 50%, transparent 74%, #eef3f8 74%, #eef3f8 75%, transparent 75%);
    }
    .line-dot {
      position: absolute;
      width: 6px;
      height: 6px;
      border-radius: 999px;
      background: var(--app-primary);
      transform: translate(-50%, 50%);
    }
    .line-dot::before {
      content: '';
      position: absolute;
      left: 50%;
      bottom: -2px;
      width: 2px;
      height: 40px;
      background: rgba(16, 163, 229, 0.15);
      transform: translateX(-50%);
    }
    .side-empty h3,
    h3 {
      margin: 0;
      padding: 0.9rem 1rem;
      border-bottom: 1px solid var(--app-border);
      font-size: var(--type-subsection-title);
      font-weight: 700;
      line-height: 1.25;
    }
    .empty-block {
      min-height: 320px;
      display: grid;
      place-content: center;
      text-align: center;
      gap: 4px;
      padding: 1rem;
      color: var(--app-muted);
    }
    .emoji {
      font-size: 2rem;
      opacity: 0.65;
    }
    .empty-block strong {
      color: var(--app-text);
      font-size: var(--type-section-title);
    }
    .tab-row {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 0.9rem 1rem;
      border-bottom: 1px solid var(--app-border);
    }
    .tab-row button {
      border: 1px solid var(--app-border);
      background: var(--app-surface-soft);
      color: var(--app-text);
      border-radius: 999px;
      padding: 0.32rem 0.8rem;
      font-size: var(--type-body);
    }
    .tab-row button.active {
      border-color: #b7dff0;
      background: #eef6ff;
      color: var(--app-primary);
    }
    .table-wrap {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      min-width: 640px;
    }
    th,
    td {
      padding: 0.65rem 0.7rem;
      border-bottom: 1px solid var(--app-border);
      text-align: left;
      color: var(--app-text);
      font-size: var(--type-body);
      white-space: nowrap;
    }
    thead tr {
      background: #e6f4fb;
    }
    .money {
      font-weight: 700;
    }
    .customer-row {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 10px;
      align-items: center;
      padding: 0.7rem 1rem;
      border-bottom: 1px solid var(--app-border);
    }
    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      display: inline-grid;
      place-items: center;
      background: var(--app-primary);
      color: var(--jci-white);
      font-weight: 700;
      font-size: 0.78rem;
    }
    .customer-meta small {
      color: var(--app-muted);
    }
    .view-link {
      display: inline-block;
      padding: 0.75rem 1rem 0.95rem;
      color: var(--app-primary);
      text-decoration: none;
      font-size: var(--type-body);
      font-weight: 500;
    }
    .map-shell {
      min-height: 220px;
      display: grid;
      grid-template-columns: 1fr 260px;
      gap: 12px;
      align-items: center;
      padding: 1rem;
    }
    .map-placeholder {
      min-height: 180px;
      border: 1px dashed var(--app-border);
      border-radius: 10px;
      display: grid;
      place-items: center;
      font-size: 3rem;
      color: #9fb2c9;
      background: #f7fbff;
    }
    .country-list strong {
      display: block;
      margin-bottom: 0.5rem;
    }
    .country-row {
      display: flex;
      justify-content: space-between;
      border-bottom: 3px solid var(--app-primary);
      padding-bottom: 0.28rem;
      font-size: var(--type-body);
    }
    .right-stack {
      display: grid;
      gap: 14px;
    }
    .donut-card {
      min-height: 280px;
      display: grid;
      grid-template-rows: auto 1fr auto;
    }
    .donut-row {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 16px;
      align-items: center;
      padding: 1rem;
    }
    .donut {
      width: 160px;
      height: 160px;
      border-radius: 999px;
      border: 12px solid var(--app-primary);
      display: grid;
      place-items: center;
      color: var(--app-text);
      font-weight: 700;
      text-align: center;
      font-size: 1.5rem;
      line-height: 1.05;
    }
    .donut span {
      font-size: 1.05rem;
      max-width: 90px;
    }
    .legend div {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 0.2rem 0;
      font-size: var(--type-body);
      color: var(--app-text);
    }
    .legend i {
      width: 10px;
      height: 10px;
      border-radius: 999px;
      background: var(--app-primary);
      display: inline-block;
    }
    .legend i.alt {
      background: #1fb3e7;
    }
    .legend small {
      color: var(--app-muted);
      display: block;
      margin-bottom: 0.3rem;
    }
    .empty {
      text-align: center;
      color: var(--app-muted);
      padding: 1rem;
    }
    @media (max-width: 1200px) {
      .grid-2-1 {
        grid-template-columns: 1fr;
      }
      .map-shell {
        grid-template-columns: 1fr;
      }
    }
    @media (max-width: 900px) {
      .page-head {
        flex-direction: column;
      }
      .filter-strip {
        flex-direction: column;
        align-items: stretch;
      }
      .filter-strip select {
        min-width: 0;
        width: 100%;
      }
      .kpi-row {
        grid-template-columns: 1fr;
        gap: 10px;
      }
      .tab-row {
        overflow-x: auto;
      }
    }
  `
})
export class SalesAnalyticsOverviewComponent implements OnInit {
  period = '30';
  topTab: 'items' | 'events' | 'subs' = 'items';
  salesLabel = this.currency(0);
  ticketsSold = 0;
  newSubscriptions = 0;
  dailySales: Array<{ pct: number }> = [];
  topItems: Array<{ name: string; qty: number; pct: number; salesValue: number; salesLabel: string }> = [];
  topCustomers: Array<{ name: string; initials: string; orders: number; salesValue: number; salesLabel: string }> = [];

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  private async load(): Promise<void> {
    const [orders, items, memberships] = await Promise.all([
      this.directus.list('event_orders', {
        fields:
          'id,status,grand_total,date_created,buyer_first_name,buyer_last_name,buyer_guest_contact_id.first_name,buyer_guest_contact_id.last_name,buyer_profile_id.first_name,buyer_profile_id.last_name',
        sort: '-date_created',
        limit: 1000
      }),
      this.directus.list('event_order_items', {
        fields: 'id,order_id,qty,price,ticket_id.name',
        limit: 2000
      }),
      this.directus.list('subscription', {
        fields: 'id,paid,created_at,total',
        sort: '-created_at',
        limit: 1000
      })
    ]);

    const paidOrderIds = new Set<string>();
    const totalsByDay = new Map<string, number>();
    const customerMap = new Map<string, { sales: number; orders: number }>();
    let orderSales = 0;

    for (const raw of orders) {
      const row = raw as Record<string, unknown>;
      const orderId = String(row['id'] ?? '');
      const status = String(row['status'] ?? '').toLowerCase();
      if (!orderId || (status !== 'paid' && status !== 'free')) continue;
      paidOrderIds.add(orderId);
      const total = Number(row['grand_total'] ?? 0);
      const safeTotal = Number.isFinite(total) ? total : 0;
      orderSales += safeTotal;

      const dateCreated = String(row['date_created'] ?? '');
      const day = this.dayKey(dateCreated);
      totalsByDay.set(day, (totalsByDay.get(day) || 0) + safeTotal);

      const customerName = this.resolveBuyerName(row);
      const current = customerMap.get(customerName) || { sales: 0, orders: 0 };
      customerMap.set(customerName, { sales: current.sales + safeTotal, orders: current.orders + 1 });
    }

    let soldTickets = 0;
    const itemMap = new Map<string, { qty: number; sales: number }>();
    for (const raw of items) {
      const row = raw as Record<string, unknown>;
      const order = row['order_id'];
      const orderId =
        typeof order === 'string'
          ? order
          : order && typeof order === 'object'
            ? String((order as Record<string, unknown>)['id'] ?? '')
            : '';
      if (!orderId || !paidOrderIds.has(orderId)) continue;

      const qty = Number(row['qty'] ?? 1);
      const safeQty = Number.isFinite(qty) ? qty : 1;
      soldTickets += safeQty;
      const price = Number(row['price'] ?? 0);
      const safePrice = Number.isFinite(price) ? price : 0;
      const name = String((row['ticket_id'] as Record<string, unknown> | undefined)?.['name'] ?? '').trim() || 'Ticket';
      const current = itemMap.get(name) || { qty: 0, sales: 0 };
      itemMap.set(name, { qty: current.qty + safeQty, sales: current.sales + safePrice * safeQty });
    }

    const paidMemberships = memberships.filter((raw) => Boolean((raw as Record<string, unknown>)['paid'] ?? false));
    const subscriptionSales = paidMemberships.reduce((sum, raw) => {
      const total = Number((raw as Record<string, unknown>)['total'] ?? 0);
      return sum + (Number.isFinite(total) ? total : 0);
    }, 0);

    this.newSubscriptions = paidMemberships.length;
    this.salesLabel = this.currency(orderSales + subscriptionSales);
    this.ticketsSold = soldTickets;

    const maxDay = Math.max(...Array.from(totalsByDay.values()), 1);
    this.dailySales = this.last30Days().map((day) => {
      const value = totalsByDay.get(day) || 0;
      return { pct: Math.max(0, (value / maxDay) * 90) };
    });

    const totalItemSales = Math.max(
      Array.from(itemMap.values()).reduce((sum, row) => sum + row.sales, 0),
      1
    );
    this.topItems = Array.from(itemMap.entries())
      .map(([name, value]) => ({
        name,
        qty: value.qty,
        pct: Math.round((value.sales / totalItemSales) * 100),
        salesValue: value.sales,
        salesLabel: this.currency(value.sales)
      }))
      .sort((a, b) => b.salesValue - a.salesValue)
      .slice(0, 5);

    this.topCustomers = Array.from(customerMap.entries())
      .map(([name, value]) => ({
        name,
        initials: this.initials(name),
        orders: value.orders,
        salesValue: value.sales,
        salesLabel: this.currency(value.sales)
      }))
      .sort((a, b) => b.salesValue - a.salesValue)
      .slice(0, 5);
  }

  private resolveBuyerName(row: Record<string, unknown>): string {
    const first =
      String(row['buyer_first_name'] ?? '').trim() ||
      String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim() ||
      String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim();
    const last =
      String(row['buyer_last_name'] ?? '').trim() ||
      String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim() ||
      String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim();
    return [first, last].filter(Boolean).join(' ').trim() || 'Guest';
  }

  private dayKey(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private last30Days(): string[] {
    const now = new Date();
    const out: string[] = [];
    for (let i = 29; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      out.push(this.dayKey(d.toISOString()));
    }
    return out;
  }

  private currency(value: number): string {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);
  }

  private initials(name: string): string {
    const parts = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '');
    return parts.join('') || 'NA';
  }
}

@Component({
  selector: 'app-sales-analytics-store-future',
  standalone: true,
  template: `
    <section class="placeholder-card">
      <div class="placeholder-head">
        <h2>Store Analytics</h2>
        <span class="coming-soon">Coming soon</span>
      </div>
      <p>Merchandise-only analytics will be added here.</p>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .placeholder-card {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 14px;
      padding: 1rem;
    }
    h2 {
      margin: 0 0 0.35rem;
      font-size: 1.1rem;
    }
    .placeholder-head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.35rem;
    }
    .placeholder-head h2 {
      margin: 0;
    }
    .coming-soon {
      border: 1px solid #b7dff0;
      background: #eef6ff;
      color: var(--jci-blue);
      font-size: 0.76rem;
      font-weight: 700;
      border-radius: 999px;
      padding: 0.1rem 0.5rem;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    p {
      margin: 0;
      color: var(--app-muted);
      font-size: 0.92rem;
    }
  `
})
export class SalesAnalyticsStoreFutureComponent {}

@Component({
  selector: 'app-sales-analytics-events',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="event-analytics-page">
      <header class="page-head">
        <h2>Event Analytics</h2>
        <p>Track ticket sales performance and attendee engagement across your events. <a href="#">Learn more</a></p>
      </header>

      <section class="top-grid">
        <article class="card key-metrics">
          <div class="card-head">
            <h3>Key metrics</h3>
            <div class="head-filters">
              <button type="button">This month</button>
              <button type="button">All events</button>
            </div>
          </div>
          <div class="kpi-grid">
            <div>
              <span>Total ticket revenue</span>
              <strong>{{ totalRevenueLabel }}</strong>
            </div>
            <div>
              <span>Tickets sold</span>
              <strong>{{ ticketsSold }}</strong>
            </div>
            <div>
              <span>Paid orders</span>
              <strong>{{ paidOrders }}</strong>
            </div>
          </div>
        </article>

        <article class="card active-card">
          <h3>Attendance</h3>
          <strong class="big">{{ checkedInCount }}/{{ attendeeCount }}</strong>
          <div class="plan-bars">
            <div class="row">
              <span>Check-in rate</span>
              <strong>{{ checkinRate }}%</strong>
            </div>
            <div class="bar"><i [style.width.%]="checkinRate"></i></div>
          </div>
          <a href="#" class="view-link">View Report</a>
        </article>
      </section>

      <section class="top-grid">
        <article class="card chart-card">
          <div class="card-head">
            <h3>Ticket revenue over the last 12 months</h3>
            <button type="button">All events</button>
          </div>
          <div class="bar-chart">
            <div *ngFor="let bucket of monthlyRevenue" class="bar-col">
              <i [style.height.%]="bucket.pct"></i>
              <span>{{ bucket.month }}</span>
            </div>
          </div>
        </article>

        <article class="card customers-card">
          <h3>Top buying customers</h3>
          <div *ngFor="let row of topCustomers" class="customer-row">
            <span class="avatar">{{ row.initials }}</span>
            <div class="meta">
              <div>{{ row.name }}</div>
              <small>Orders: {{ row.orders }}</small>
            </div>
            <strong>{{ row.salesLabel }}</strong>
          </div>
          <div *ngIf="topCustomers.length === 0" class="empty">No customer data.</div>
          <a href="#" class="view-link">View Report</a>
        </article>
      </section>

      <section class="top-grid">
        <article class="card">
          <div class="card-head">
            <h3>Top performing events</h3>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Tickets</th>
                  <th>Orders</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of topEvents">
                  <td>{{ row.name }}</td>
                  <td>{{ row.tickets }}</td>
                  <td>{{ row.orders }}</td>
                  <td class="money">{{ row.revenueLabel }}</td>
                </tr>
                <tr *ngIf="topEvents.length === 0">
                  <td colspan="4" class="empty">No event performance data.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <a href="#" class="view-link">View Report</a>
        </article>

        <div class="right-stack">
          <article class="card donut-card">
            <h3>Sales breakdown by coupon</h3>
            <div class="donut-row">
              <div class="donut"><span>{{ totalRevenueLabel }}</span></div>
              <div class="legend">
                <div><i></i> No coupon</div>
                <small>100% · {{ totalRevenueLabel }}</small>
              </div>
            </div>
            <a href="#" class="view-link">View Report</a>
          </article>
          <article class="card donut-card">
            <h3>New vs returning buyers</h3>
            <div class="donut-row">
              <div class="donut"><span>{{ uniqueBuyers }}</span></div>
              <div class="legend">
                <div><i></i> New</div>
                <small>{{ newBuyerPct }}% · {{ newBuyerCount }}</small>
                <div><i class="alt"></i> Returning</div>
                <small>{{ returningBuyerPct }}% · {{ returningBuyerCount }}</small>
              </div>
            </div>
            <a href="#" class="view-link">View Report</a>
          </article>
        </div>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }
    .event-analytics-page {
      display: grid;
      gap: 14px;
    }
    .page-head h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      font-weight: 700;
    }
    .page-head p {
      margin: 0.3rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }
    .page-head a {
      color: var(--app-primary);
      text-decoration: none;
    }
    .top-grid {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 14px;
    }
    .card {
      border: 1px solid var(--app-border);
      border-radius: 12px;
      background: var(--app-surface);
      overflow: hidden;
    }
    .card-head {
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
    }
    .card-head h3 {
      margin: 0;
      font-size: var(--type-subsection-title);
      font-weight: 700;
      line-height: 1.2;
    }
    .head-filters {
      display: flex;
      gap: 8px;
    }
    .head-filters button,
    .card-head button {
      border: 0;
      background: transparent;
      color: var(--app-primary);
      font-size: var(--type-body);
    }
    .kpi-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    .kpi-grid > div {
      padding: 1rem;
      border-right: 1px solid var(--app-border);
    }
    .kpi-grid > div:last-child {
      border-right: 0;
    }
    .kpi-grid span {
      display: block;
      color: var(--app-muted);
      font-size: var(--type-body);
      margin-bottom: 3px;
    }
    .kpi-grid strong {
      font-size: 2rem;
      line-height: 1.05;
      font-weight: 700;
    }
    .active-card h3 {
      margin: 0;
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      font-size: var(--type-subsection-title);
    }
    .active-card .big {
      display: block;
      font-size: 2rem;
      padding: 0.55rem 1rem;
      line-height: 1;
    }
    .plan-bars {
      padding: 0.25rem 1rem 0.4rem;
      display: grid;
      gap: 10px;
    }
    .plan-bars .row {
      display: flex;
      justify-content: space-between;
      color: var(--app-text);
      font-size: var(--type-body);
    }
    .bar {
      height: 6px;
      background: #edf3fa;
      border-radius: 999px;
      overflow: hidden;
    }
    .bar i {
      display: block;
      height: 100%;
      background: var(--app-primary);
    }
    .view-link {
      display: inline-block;
      padding: 0.75rem 1rem 0.9rem;
      color: var(--app-primary);
      text-decoration: none;
      font-size: var(--type-body);
    }
    .chart-card .bar-chart {
      min-height: 300px;
      padding: 1rem;
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      align-items: end;
      gap: 10px;
    }
    .bar-col {
      display: grid;
      justify-items: center;
      gap: 8px;
    }
    .bar-col i {
      width: 26px;
      min-height: 3px;
      max-height: 200px;
      background: #5f68ba;
      border-radius: 4px 4px 0 0;
    }
    .bar-col span {
      color: var(--app-muted);
      font-size: 0.85rem;
    }
    .customers-card h3 {
      margin: 0;
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      font-size: var(--type-subsection-title);
    }
    .customer-row {
      padding: 0.72rem 1rem;
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 10px;
      align-items: center;
      border-bottom: 1px solid var(--app-border);
    }
    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      background: var(--app-primary);
      color: var(--jci-white);
      display: grid;
      place-items: center;
      font-weight: 700;
      font-size: 0.78rem;
    }
    .meta small {
      color: var(--app-muted);
    }
    .table-wrap {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      min-width: 600px;
    }
    th,
    td {
      border-bottom: 1px solid var(--app-border);
      padding: 0.62rem 0.68rem;
      text-align: left;
      font-size: var(--type-body);
      color: var(--app-text);
      white-space: nowrap;
    }
    th {
      background: #f5f9ff;
    }
    .money {
      font-weight: 700;
    }
    .right-stack {
      display: grid;
      gap: 14px;
    }
    .donut-card {
      min-height: 280px;
      display: grid;
      grid-template-rows: auto 1fr auto;
    }
    .donut-card h3 {
      margin: 0;
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      font-size: var(--type-subsection-title);
    }
    .donut-row {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 16px;
      align-items: center;
      padding: 1rem;
    }
    .donut {
      width: 160px;
      height: 160px;
      border-radius: 999px;
      border: 12px solid var(--app-primary);
      display: grid;
      place-items: center;
      color: var(--app-text);
      font-weight: 700;
      text-align: center;
      font-size: 1.5rem;
      line-height: 1.05;
    }
    .donut span {
      font-size: 1.05rem;
      max-width: 90px;
    }
    .legend div {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 0.2rem 0;
      font-size: var(--type-body);
      color: var(--app-text);
    }
    .legend i {
      width: 10px;
      height: 10px;
      border-radius: 999px;
      background: var(--app-primary);
      display: inline-block;
    }
    .legend i.alt {
      background: #1fb3e7;
    }
    .legend small {
      color: var(--app-muted);
      display: block;
      margin-bottom: 0.3rem;
    }
    .empty {
      color: var(--app-muted);
      padding: 0.75rem 1rem;
      font-size: var(--type-body);
      text-align: center;
    }
    @media (max-width: 1200px) {
      .top-grid {
        grid-template-columns: 1fr;
      }
    }
    @media (max-width: 900px) {
      .kpi-grid {
        grid-template-columns: 1fr;
      }
      .kpi-grid > div {
        border-right: 0;
        border-top: 1px solid var(--app-border);
      }
      .kpi-grid > div:first-child {
        border-top: 0;
      }
      .chart-card .bar-chart {
        gap: 6px;
      }
      .bar-col i {
        width: 18px;
      }
    }
  `
})
export class SalesAnalyticsEventsComponent implements OnInit {
  totalRevenueLabel = this.currency(0);
  ticketsSold = 0;
  paidOrders = 0;
  attendeeCount = 0;
  checkedInCount = 0;
  checkinRate = 0;
  uniqueBuyers = 0;
  newBuyerCount = 0;
  returningBuyerCount = 0;
  newBuyerPct = 0;
  returningBuyerPct = 0;
  monthlyRevenue: Array<{ month: string; value: number; pct: number }> = [];
  topEvents: Array<{ name: string; tickets: number; orders: number; revenue: number; revenueLabel: string }> = [];
  topCustomers: Array<{ name: string; initials: string; orders: number; salesValue: number; salesLabel: string }> = [];

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    const [ordersRaw, itemsRaw, attendeesRaw] = await Promise.all([
      this.directus.list('event_orders', {
        fields:
          'id,event_id.name,status,grand_total,date_created,buyer_first_name,buyer_last_name,buyer_guest_contact_id.first_name,buyer_guest_contact_id.last_name,buyer_profile_id.first_name,buyer_profile_id.last_name',
        sort: '-date_created',
        limit: 2000
      }),
      this.directus.list('event_order_items', {
        fields: 'id,order_id,qty,price,ticket_id.name',
        limit: 4000
      }),
      this.directus.list('event_attendees', {
        fields: 'id,order_id,checkin_status,checked_in,is_checked_in,attended',
        limit: 4000
      })
    ]);

    const orders = ordersRaw.map((r) => r as Record<string, unknown>);
    const items = itemsRaw.map((r) => r as Record<string, unknown>);
    const attendees = attendeesRaw.map((r) => r as Record<string, unknown>);

    const validOrderIds = new Set<string>();
    const paidOrderIds = new Set<string>();
    const monthlyTotals = new Map<string, number>();
    const customerTotals = new Map<string, { sales: number; orders: number }>();
    const eventTotals = new Map<string, { tickets: number; orders: number; revenue: number }>();
    const orderToEvent = new Map<string, string>();
    let totalRevenue = 0;

    for (const row of orders) {
      const id = String(row['id'] ?? '').trim();
      if (!id) continue;
      const status = String(row['status'] ?? '').trim().toLowerCase();
      if (status === 'cancelled' || status === 'canceled' || status === 'removed' || status === 'refunded') continue;
      validOrderIds.add(id);

      const eventName = String((row['event_id'] as Record<string, unknown> | undefined)?.['name'] ?? '').trim() || 'Event';
      orderToEvent.set(id, eventName);

      if (status === 'paid' || status === 'free') {
        paidOrderIds.add(id);
      }

      const total = Number(row['grand_total'] ?? 0);
      const safeTotal = Number.isFinite(total) ? total : 0;
      totalRevenue += safeTotal;
      const customer = this.resolveBuyerName(row);
      const currentCustomer = customerTotals.get(customer) || { sales: 0, orders: 0 };
      customerTotals.set(customer, { sales: currentCustomer.sales + safeTotal, orders: currentCustomer.orders + 1 });

      const eventCurrent = eventTotals.get(eventName) || { tickets: 0, orders: 0, revenue: 0 };
      eventTotals.set(eventName, { ...eventCurrent, orders: eventCurrent.orders + 1, revenue: eventCurrent.revenue + safeTotal });

      const monthKey = this.monthKey(String(row['date_created'] ?? ''));
      monthlyTotals.set(monthKey, (monthlyTotals.get(monthKey) || 0) + safeTotal);
    }

    let soldTickets = 0;
    for (const row of items) {
      const orderId = this.relationId(row['order_id']);
      if (!orderId || !validOrderIds.has(orderId)) continue;
      const qty = Number(row['qty'] ?? 1);
      const safeQty = Number.isFinite(qty) ? qty : 1;
      soldTickets += safeQty;
      const eventName = orderToEvent.get(orderId) || 'Event';
      const currentEvent = eventTotals.get(eventName) || { tickets: 0, orders: 0, revenue: 0 };
      eventTotals.set(eventName, { ...currentEvent, tickets: currentEvent.tickets + safeQty });
    }

    let checkedIn = 0;
    let attendeeTotal = 0;
    for (const row of attendees) {
      const orderId = this.relationId(row['order_id']);
      if (!orderId || !validOrderIds.has(orderId)) continue;
      attendeeTotal += 1;
      const rawCheckin = String(row['checkin_status'] ?? '').trim().toLowerCase();
      const isChecked =
        rawCheckin === 'checked_in' || Boolean(row['checked_in'] ?? row['is_checked_in'] ?? row['attended'] ?? false);
      if (isChecked) checkedIn += 1;
    }

    this.totalRevenueLabel = this.currency(totalRevenue);
    this.paidOrders = paidOrderIds.size;
    this.ticketsSold = soldTickets;
    this.attendeeCount = attendeeTotal;
    this.checkedInCount = checkedIn;
    this.checkinRate = attendeeTotal ? Math.round((checkedIn / attendeeTotal) * 100) : 0;

    const buyers = Array.from(customerTotals.values());
    this.uniqueBuyers = buyers.length;
    this.newBuyerCount = buyers.filter((b) => b.orders === 1).length;
    this.returningBuyerCount = buyers.filter((b) => b.orders > 1).length;
    this.newBuyerPct = buyers.length ? Math.round((this.newBuyerCount / buyers.length) * 100) : 0;
    this.returningBuyerPct = buyers.length ? Math.round((this.returningBuyerCount / buyers.length) * 100) : 0;

    const last12 = this.last12Months();
    const maxMonthValue = Math.max(...last12.map((m) => monthlyTotals.get(m.key) || 0), 1);
    this.monthlyRevenue = last12.map((m) => {
      const value = monthlyTotals.get(m.key) || 0;
      return { month: m.label, value, pct: (value / maxMonthValue) * 95 };
    });

    this.topEvents = Array.from(eventTotals.entries())
      .map(([name, value]) => ({ ...value, name, revenueLabel: this.currency(value.revenue) }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 6);

    this.topCustomers = Array.from(customerTotals.entries())
      .map(([name, value]) => ({
        name,
        initials: this.initials(name),
        orders: value.orders,
        salesValue: value.sales,
        salesLabel: this.currency(value.sales)
      }))
      .sort((a, b) => b.salesValue - a.salesValue)
      .slice(0, 5);
  }

  private relationId(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') return String((value as Record<string, unknown>)['id'] ?? '').trim();
    return '';
  }

  private resolveBuyerName(row: Record<string, unknown>): string {
    const first =
      String(row['buyer_first_name'] ?? '').trim() ||
      String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim() ||
      String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['first_name'] ?? '').trim();
    const last =
      String(row['buyer_last_name'] ?? '').trim() ||
      String((row['buyer_guest_contact_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim() ||
      String((row['buyer_profile_id'] as Record<string, unknown> | undefined)?.['last_name'] ?? '').trim();
    return [first, last].filter(Boolean).join(' ').trim() || 'Guest';
  }

  private monthKey(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  private last12Months(): Array<{ key: string; label: string }> {
    const out: Array<{ key: string; label: string }> = [];
    const now = new Date();
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-US', { month: 'short' })
      });
    }
    return out;
  }

  private currency(value: number): string {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);
  }

  private initials(name: string): string {
    const parts = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '');
    return parts.join('') || 'NA';
  }
}

@Component({
  selector: 'app-sales-analytics-memberships',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="membership-analytics-page">
      <header class="page-head">
        <h2>Subscriptions Analytics</h2>
        <p>Track and analyze the performance of pricing plans and other recurring subscriptions. <a href="#">Learn more</a></p>
      </header>

      <section class="top-grid">
        <article class="card key-metrics">
          <div class="card-head">
            <h3>Key metrics</h3>
            <div class="head-filters">
              <button type="button">This month</button>
              <button type="button">All plans</button>
            </div>
          </div>
          <div class="kpi-grid">
            <div>
              <span>Total revenue</span>
              <strong>{{ totalRevenueLabel }}</strong>
            </div>
            <div>
              <span>New subscriptions</span>
              <strong>{{ newSubscriptions }}</strong>
            </div>
            <div>
              <span>Ended subscriptions</span>
              <strong>{{ endedSubscriptions }}</strong>
            </div>
          </div>
        </article>

        <article class="card active-card">
          <h3>Active subscriptions</h3>
          <strong class="big">{{ activeSubscriptions }}</strong>
          <div class="plan-bars">
            <div *ngFor="let plan of activePlans">
              <div class="row">
                <span>{{ plan.name }}</span>
                <strong>{{ plan.count }}</strong>
              </div>
              <div class="bar"><i [style.width.%]="plan.pct"></i></div>
            </div>
            <div *ngIf="activePlans.length === 0" class="empty">No active plans.</div>
          </div>
          <a href="#" class="view-link">View Report</a>
        </article>
      </section>

      <section class="top-grid">
        <article class="card chart-card">
          <div class="card-head">
            <h3>Revenue over the last 12 months</h3>
            <button type="button">All plans</button>
          </div>
          <div class="bar-chart">
            <div *ngFor="let bucket of monthlyRevenue" class="bar-col">
              <i [style.height.%]="bucket.pct"></i>
              <span>{{ bucket.month }}</span>
            </div>
          </div>
        </article>

        <article class="card customers-card">
          <h3>Top paying customers</h3>
          <div *ngFor="let row of topCustomers" class="customer-row">
            <span class="avatar">{{ row.initials }}</span>
            <div class="meta">
              <div>{{ row.name }}</div>
              <small>Orders: {{ row.orders }}</small>
            </div>
            <strong>{{ row.salesLabel }}</strong>
          </div>
          <div *ngIf="topCustomers.length === 0" class="empty">No customer data.</div>
          <a href="#" class="view-link">View Report</a>
        </article>
      </section>

      <section class="retention-section">
        <h3>Customer retention</h3>
        <div class="retention-grid">
          <article class="card mini-card">
            <div class="mini-head">
              <span>Ending soon</span>
              <button type="button">Next 30 days</button>
            </div>
            <div *ngFor="let row of endingSoon" class="mini-row">
              <span class="avatar">{{ row.initials }}</span>
              <div>
                <strong>{{ row.name }}</strong>
                <small>{{ row.plan }}</small>
              </div>
            </div>
            <div *ngIf="endingSoon.length === 0" class="empty">No subscriptions ending soon.</div>
            <a href="#" class="view-link">See All ({{ endingSoon.length }})</a>
          </article>

          <article class="card mini-card">
            <div class="mini-head">
              <span>Recently ended</span>
              <button type="button">Last 30 days</button>
            </div>
            <div class="metric-row">
              <span>Expired</span>
              <strong>{{ endedSubscriptions }}</strong>
            </div>
            <a href="#" class="view-link">View Report</a>
          </article>

          <article class="card mini-card">
            <div class="mini-head">
              <span>Customer value</span>
            </div>
            <div class="value-row">
              <span>Average revenue per subscription</span>
              <strong>{{ avgRevenuePerSubscriptionLabel }}</strong>
            </div>
            <div class="value-row">
              <span>Average subscription lifetime</span>
              <strong>{{ avgLifetimeLabel }}</strong>
            </div>
            <div class="value-row">
              <span>Customer lifetime value</span>
              <strong>{{ avgRevenuePerSubscriptionLabel }}</strong>
            </div>
            <a href="#" class="view-link">Learn More About These Metrics</a>
          </article>
        </div>
      </section>

      <section class="card cohort-card">
        <div class="card-head">
          <div>
            <h3>Retention over time</h3>
            <p>See how groups of customers who subscribed in the same month stayed over time. <a href="#">Learn more</a></p>
          </div>
          <div class="toggle">
            <button type="button" class="active">%</button>
            <button type="button">#</button>
          </div>
        </div>
        <div class="cohort-wrap">
          <table>
            <thead>
              <tr>
                <th>Starting month</th>
                <th>New subscriptions</th>
                <th *ngFor="let month of cohortMonths">{{ month }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of cohortRows">
                <td>{{ row.label }}</td>
                <td>{{ row.count }}</td>
                <td *ngFor="let pct of row.values" [class.filled]="pct > 0">{{ pct }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }
    .membership-analytics-page {
      display: grid;
      gap: 14px;
    }
    .page-head h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      font-weight: 700;
    }
    .page-head p {
      margin: 0.3rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }
    .page-head a {
      color: var(--app-primary);
      text-decoration: none;
    }
    .top-grid {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 14px;
    }
    .card {
      border: 1px solid var(--app-border);
      border-radius: 12px;
      background: var(--app-surface);
      overflow: hidden;
    }
    .card-head {
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
    }
    .card-head h3 {
      margin: 0;
      font-size: var(--type-subsection-title);
      font-weight: 700;
      line-height: 1.2;
    }
    .card-head p {
      margin: 0.2rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }
    .head-filters,
    .card-head button {
      display: flex;
      gap: 8px;
    }
    .head-filters button,
    .card-head button {
      border: 0;
      background: transparent;
      color: var(--app-primary);
      font-size: var(--type-body);
    }
    .kpi-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    .kpi-grid > div {
      padding: 1rem;
      border-right: 1px solid var(--app-border);
    }
    .kpi-grid > div:last-child {
      border-right: 0;
    }
    .kpi-grid span {
      display: block;
      color: var(--app-muted);
      font-size: var(--type-body);
      margin-bottom: 3px;
    }
    .kpi-grid strong {
      font-size: 2rem;
      line-height: 1.05;
      font-weight: 700;
    }
    .active-card h3 {
      margin: 0;
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      font-size: var(--type-subsection-title);
    }
    .active-card .big {
      display: block;
      font-size: 2rem;
      padding: 0.55rem 1rem;
      line-height: 1;
    }
    .plan-bars {
      padding: 0.25rem 1rem 0.4rem;
      display: grid;
      gap: 10px;
    }
    .plan-bars .row {
      display: flex;
      justify-content: space-between;
      color: var(--app-text);
      font-size: var(--type-body);
    }
    .bar {
      height: 6px;
      background: #edf3fa;
      border-radius: 999px;
      overflow: hidden;
    }
    .bar i {
      display: block;
      height: 100%;
      background: var(--app-primary);
    }
    .view-link {
      display: inline-block;
      padding: 0.75rem 1rem 0.9rem;
      color: var(--app-primary);
      text-decoration: none;
      font-size: var(--type-body);
    }
    .chart-card .bar-chart {
      min-height: 300px;
      padding: 1rem;
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      align-items: end;
      gap: 10px;
    }
    .bar-col {
      display: grid;
      justify-items: center;
      gap: 8px;
    }
    .bar-col i {
      width: 26px;
      min-height: 3px;
      max-height: 200px;
      background: #5f68ba;
      border-radius: 4px 4px 0 0;
    }
    .bar-col span {
      color: var(--app-muted);
      font-size: 0.85rem;
    }
    .customers-card h3 {
      margin: 0;
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      font-size: var(--type-subsection-title);
    }
    .customer-row {
      padding: 0.72rem 1rem;
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 10px;
      align-items: center;
      border-bottom: 1px solid var(--app-border);
    }
    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      background: var(--app-primary);
      color: var(--jci-white);
      display: grid;
      place-items: center;
      font-weight: 700;
      font-size: 0.78rem;
    }
    .meta small {
      color: var(--app-muted);
    }
    .retention-section > h3 {
      margin: 0;
      font-size: var(--type-section-title);
      font-weight: 700;
      line-height: 1.2;
      padding: 0 0.1rem;
    }
    .retention-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 14px;
    }
    .mini-head {
      padding: 0.85rem 1rem;
      border-bottom: 1px solid var(--app-border);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
    }
    .mini-head span {
      font-size: var(--type-subsection-title);
      font-weight: 700;
    }
    .mini-head button {
      border: 1px solid var(--app-border);
      background: var(--app-surface-soft);
      border-radius: 999px;
      padding: 0.25rem 0.7rem;
      color: var(--app-text);
      font-size: var(--type-body);
    }
    .mini-row {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 10px;
      align-items: center;
      padding: 0.75rem 1rem 0.2rem;
    }
    .mini-row strong,
    .metric-row span,
    .value-row span {
      font-size: var(--type-body);
    }
    .mini-row small {
      color: var(--app-muted);
      display: block;
    }
    .metric-row,
    .value-row {
      padding: 0.72rem 1rem;
      border-bottom: 1px solid var(--app-border);
      display: flex;
      justify-content: space-between;
      gap: 10px;
      align-items: center;
    }
    .cohort-card .cohort-wrap {
      padding: 0.9rem 1rem;
      overflow-x: auto;
    }
    .cohort-card table {
      width: 100%;
      border-collapse: collapse;
      min-width: 980px;
    }
    .cohort-card th,
    .cohort-card td {
      border-bottom: 1px solid var(--app-border);
      padding: 0.55rem 0.6rem;
      text-align: left;
      font-size: var(--type-body);
      color: var(--app-text);
      white-space: nowrap;
    }
    .cohort-card th {
      background: #f5f9ff;
    }
    .cohort-card td.filled {
      background: #1f7ae2;
      color: #fff;
    }
    .toggle {
      border: 1px solid var(--app-border);
      border-radius: 8px;
      overflow: hidden;
      display: inline-flex;
    }
    .toggle button {
      border: 0;
      background: var(--app-surface-soft);
      color: var(--app-text);
      width: 34px;
      height: 32px;
    }
    .toggle .active {
      background: #eef6ff;
      color: var(--app-primary);
      font-weight: 700;
    }
    .empty {
      color: var(--app-muted);
      padding: 0.75rem 1rem;
      font-size: var(--type-body);
    }
    @media (max-width: 1200px) {
      .top-grid,
      .retention-grid {
        grid-template-columns: 1fr;
      }
    }
    @media (max-width: 900px) {
      .kpi-grid {
        grid-template-columns: 1fr;
      }
      .kpi-grid > div {
        border-right: 0;
        border-top: 1px solid var(--app-border);
      }
      .kpi-grid > div:first-child {
        border-top: 0;
      }
      .chart-card .bar-chart {
        gap: 6px;
      }
      .bar-col i {
        width: 18px;
      }
    }
  `
})
export class SalesAnalyticsMembershipsComponent implements OnInit {
  totalRevenueLabel = this.currency(0);
  newSubscriptions = 0;
  endedSubscriptions = 0;
  activeSubscriptions = 0;
  avgRevenuePerSubscriptionLabel = this.currency(0);
  avgLifetimeLabel = '—';
  activePlans: Array<{ name: string; count: number; pct: number }> = [];
  monthlyRevenue: Array<{ month: string; value: number; pct: number }> = [];
  topCustomers: Array<{ name: string; initials: string; orders: number; salesValue: number; salesLabel: string }> = [];
  endingSoon: Array<{ name: string; initials: string; plan: string }> = [];
  cohortMonths = Array.from({ length: 12 }, (_, i) => `${i + 1}`);
  cohortRows: Array<{ label: string; count: number; values: number[] }> = [];

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    const rows = await this.directus.list('subscription', {
      fields: 'id,profile_id.first_name,profile_id.last_name,profile_name,email,type,year,total,paid,created_at',
      sort: '-created_at',
      limit: 1000
    });
    this.hydrate(rows.map((raw) => raw as Record<string, unknown>));
  }

  private hydrate(rows: Array<Record<string, unknown>>): void {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const endedWindowStart = new Date(now);
    endedWindowStart.setDate(now.getDate() - 30);

    const monthlyTotals = new Map<string, number>();
    const customerTotals = new Map<string, { sales: number; orders: number }>();
    const planCounts = new Map<string, number>();
    const lifetimesInMonths: number[] = [];
    const cohorts = new Map<string, { count: number; starts: Date[]; ends: Date[] }>();

    let totalRevenue = 0;
    let newSubs = 0;
    let ended = 0;
    let active = 0;
    let paidCount = 0;

    for (const row of rows) {
      const created = this.parseDate(String(row['created_at'] ?? ''));
      if (!created) continue;
      const end = this.subscriptionEndDate(row, created);
      const plan = String(row['type'] ?? '').trim() || 'Membership';
      const paid = Boolean(row['paid'] ?? false);
      const total = Number(row['total'] ?? 0);
      const safeTotal = Number.isFinite(total) ? total : 0;
      const customer = this.resolveCustomerName(row);

      if (paid) {
        totalRevenue += safeTotal;
        paidCount += 1;
      }
      if (created >= monthStart) newSubs += 1;
      if (end >= endedWindowStart && end <= now) ended += 1;
      if (end >= now) {
        active += 1;
        planCounts.set(plan, (planCounts.get(plan) || 0) + 1);
      }

      const monthKey = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
      monthlyTotals.set(monthKey, (monthlyTotals.get(monthKey) || 0) + safeTotal);

      const customerCurrent = customerTotals.get(customer) || { sales: 0, orders: 0 };
      customerTotals.set(customer, { sales: customerCurrent.sales + safeTotal, orders: customerCurrent.orders + 1 });

      const lifetimeMonths = Math.max(1, this.monthDiff(created, end));
      lifetimesInMonths.push(lifetimeMonths);

      const cohortKey = this.monthLabel(created);
      const cohort = cohorts.get(cohortKey) || { count: 0, starts: [], ends: [] };
      cohort.count += 1;
      cohort.starts.push(created);
      cohort.ends.push(end);
      cohorts.set(cohortKey, cohort);
    }

    this.totalRevenueLabel = this.currency(totalRevenue);
    this.newSubscriptions = newSubs;
    this.endedSubscriptions = ended;
    this.activeSubscriptions = active;
    this.avgRevenuePerSubscriptionLabel = this.currency(paidCount ? totalRevenue / paidCount : 0);
    this.avgLifetimeLabel = this.formatLifetime(lifetimesInMonths);

    const activePlanTotal = Math.max(
      Array.from(planCounts.values()).reduce((sum, val) => sum + val, 0),
      1
    );
    this.activePlans = Array.from(planCounts.entries())
      .map(([name, count]) => ({ name, count, pct: (count / activePlanTotal) * 100 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);

    const last12 = this.last12Months();
    const maxMonthValue = Math.max(...last12.map((m) => monthlyTotals.get(m.key) || 0), 1);
    this.monthlyRevenue = last12.map((m) => {
      const value = monthlyTotals.get(m.key) || 0;
      return { month: m.label, value, pct: (value / maxMonthValue) * 95 };
    });

    this.topCustomers = Array.from(customerTotals.entries())
      .map(([name, value]) => ({
        name,
        initials: this.initials(name),
        orders: value.orders,
        salesValue: value.sales,
        salesLabel: this.currency(value.sales)
      }))
      .sort((a, b) => b.salesValue - a.salesValue)
      .slice(0, 5);

    this.endingSoon = rows
      .map((row) => {
        const created = this.parseDate(String(row['created_at'] ?? ''));
        if (!created) return null;
        const end = this.subscriptionEndDate(row, created);
        if (end < now || end > new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30)) return null;
        return {
          name: this.resolveCustomerName(row),
          initials: this.initials(this.resolveCustomerName(row)),
          plan: String(row['type'] ?? '').trim() || 'Membership'
        };
      })
      .filter((row): row is { name: string; initials: string; plan: string } => Boolean(row))
      .slice(0, 5);

    this.cohortRows = Array.from(cohorts.entries()).map(([label, info]) => ({
      label,
      count: info.count,
      values: this.buildCohortValues(info.starts, info.ends)
    }));
    this.cohortRows.sort((a, b) => this.parseMonthLabel(a.label).getTime() - this.parseMonthLabel(b.label).getTime());
  }

  private resolveCustomerName(row: Record<string, unknown>): string {
    const profile = (row['profile_id'] as Record<string, unknown> | undefined) || {};
    const first = String(profile['first_name'] ?? '').trim();
    const last = String(profile['last_name'] ?? '').trim();
    const profileName = String(row['profile_name'] ?? '').trim();
    const email = String(row['email'] ?? '').trim();
    return [first, last].filter(Boolean).join(' ').trim() || profileName || email || 'Guest';
  }

  private subscriptionEndDate(row: Record<string, unknown>, created: Date): Date {
    const yearRaw = Number(row['year'] ?? 0);
    if (Number.isFinite(yearRaw) && yearRaw > 0) {
      return new Date(yearRaw, 11, 31, 23, 59, 59, 999);
    }
    const end = new Date(created);
    end.setFullYear(end.getFullYear() + 1);
    return end;
  }

  private parseDate(value: string): Date | null {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }

  private last12Months(): Array<{ key: string; label: string }> {
    const out: Array<{ key: string; label: string }> = [];
    const now = new Date();
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-US', { month: 'short' })
      });
    }
    return out;
  }

  private monthLabel(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  private parseMonthLabel(label: string): Date {
    return new Date(`${label} 1`);
  }

  private buildCohortValues(starts: Date[], ends: Date[]): number[] {
    const values: number[] = [];
    for (let m = 1; m <= 12; m += 1) {
      let activeCount = 0;
      for (let i = 0; i < starts.length; i += 1) {
        const windowDate = new Date(starts[i]);
        windowDate.setMonth(windowDate.getMonth() + (m - 1));
        if (ends[i] >= windowDate) activeCount += 1;
      }
      values.push(starts.length ? Math.round((activeCount / starts.length) * 100) : 0);
    }
    return values;
  }

  private monthDiff(start: Date, end: Date): number {
    return Math.max(1, end.getMonth() - start.getMonth() + 12 * (end.getFullYear() - start.getFullYear()) + 1);
  }

  private formatLifetime(months: number[]): string {
    if (!months.length) return '—';
    const avg = Math.round(months.reduce((sum, m) => sum + m, 0) / months.length);
    const years = Math.floor(avg / 12);
    const rem = avg % 12;
    if (years && rem) return `${years} year ${rem} month`;
    if (years) return `${years} year`;
    return `${rem} month`;
  }

  private currency(value: number): string {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);
  }

  private initials(name: string): string {
    const parts = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '');
    return parts.join('') || 'NA';
  }
}
